import {useCallback} from "react";

import {GuideStateApi} from "@repo/guide-state";

import {GUIDE_ACKNOWLEDGEMENT_PROMO_NAME} from "../constants.ts";
import {useSetPromotionMutation} from "../queries/use-set-promotion-mutation.ts";

import {useGuideTermsMessages} from "./use-guide-terms-messages.ts";

/**
 * Manage guide terms acknowledgement.
 *
 * This hook gets the current state of whether or not a user has accepted the
 * ai terms and updates the global guide state when the terms are accepted via
 * the returned function.
 *
 * @returns A function for marking the user's acceptance of the AI guide terms.
 * When called, and the user has not yet accepted the guide terms, this function
 * invokes a mutation to do so and updates the global guide state accordingly.
 * If the terms are already accepted, this function is a no-op.
 */
export function useAcknowledgeGuideTerms(): () => Promise<any> {
    const guideTermsMessages = useGuideTermsMessages();

    const acceptGuideAcknowledgementMutationFn = useSetPromotionMutation(
        GUIDE_ACKNOWLEDGEMENT_PROMO_NAME,
    );

    const acceptGuideAcknowledgement = useCallback(async () => {
        GuideStateApi.set("conversation", (conversation) => {
            // We want to preserve the guide acknowledgement conversation so that:
            // 1. allow the user to refer back to the guide acknowledgement they just accepted
            // 2. give them a confirmation bubble to reflect the interaction
            // 3. display the original conversation to preserve the original
            // user workflow
            return [...guideTermsMessages, ...conversation];
        });

        if (GuideStateApi.latest().hasAcknowledgedGuide) {
            return Promise.resolve();
        }

        await acceptGuideAcknowledgementMutationFn();
        GuideStateApi.set("hasAcknowledgedGuide", true);
    }, [guideTermsMessages, acceptGuideAcknowledgementMutationFn]);

    return acceptGuideAcknowledgement;
}
